"use client";

import { Core } from "@faire/design-tokens";
import { Column, IFlexProps, Row } from "@faire/web/slate/Layout";
import {
  Typography,
  TypographyStyles,
} from "@faire/web/slate/Typography";
import React, { type JSX } from "react";
import styled, { css } from "styled-components";

import { Close } from "../icons/deprecated/Close";
import { themeOrDefault } from "../Theme";
import {
  highVisSlateComponent,
  HIGH_VIS_FILTER,
} from "../utils/highVisSlateComponent";

/* eslint-disable @faire/enforce-no-supersededby-components */

export const ClearWrapper = styled(Column).attrs({
  align: "center",
  justify: "center",
})`
  width: 26px;
  height: 26px;
  border-radius: 50%;
`;

/**
 * @trackcomponent
 * @supersededBy "@faire/web/slate/IconButton/IconButton#IconButton"
 */
export const ClearButton: React.FC<{
  onClick: () => void;
  "data-test-id"?: string;
}> = (props) => {
  const handleClick: React.MouseEventHandler = (event) => {
    event.stopPropagation();
    event.preventDefault();
    props.onClick();
  };
  return (
    <ClearWrapper onClick={handleClick} data-test-id={props["data-test-id"]}>
      <Close fontSize="10px" strokeWidth={1} />
    </ClearWrapper>
  );
};

export const Label = styled(Typography).attrs({
  color: "inherit",
})`
  &&& {
    ${({ theme }) => TypographyStyles(theme.pills?.filter?.label ?? {})};
    color: inherit;
  }
`;

export const IconWrapper = styled(Row).attrs({ align: "center" })<{
  $hasFilterSelected?: boolean;
}>`
  margin-right: 8px;
  ${({ $hasFilterSelected, theme }) => {
    const regular = theme.pills.filter.secondary.contrast ?? Core.text.default;
    const active = theme.pills.filter.primary.contrast ?? Core.icon.inverse;

    return css`
      color: ${$hasFilterSelected ? active : regular};
    `;
  }}
`;

interface IProps extends Omit<IFlexProps, "align"> {
  isOpen?: boolean;
  hasFilterSelected?: boolean;
  displayCloseButton?: boolean;
  alwaysShowCloseButton?: boolean;
}

export const Pill: React.FC<IProps & React.RefAttributes<HTMLDivElement>> = ({
  isOpen,
  hasFilterSelected,
  displayCloseButton,
  alwaysShowCloseButton,
  as,
  ref,
  ...rest
}) => {
  return (
    <BasePill
      ref={ref}
      forwardedAs={as as keyof JSX.IntrinsicElements}
      {...rest}
      $isOpen={isOpen}
      $hasFilterSelected={hasFilterSelected}
      $displayCloseButton={displayCloseButton}
      $alwaysShowCloseButton={alwaysShowCloseButton}
    />
  );
};

const BasePill = styled(Row).attrs({ align: "center" })<{
  $isOpen?: boolean;
  $hasFilterSelected?: boolean;
  $displayCloseButton?: boolean;
  $alwaysShowCloseButton?: boolean;
}>`
  border-radius: 100px;
  cursor: pointer;
  padding: ${(props) =>
    !props.$displayCloseButton ? "8px 16px" : "8px 8px 8px 16px"};
  height: 40px;

  ${ClearWrapper} {
    &:hover {
      background-color: ${({
        theme,
        $alwaysShowCloseButton: alwaysShowCloseButton,
        $hasFilterSelected: hasFilterSelected,
      }) =>
        alwaysShowCloseButton && !hasFilterSelected
          ? theme.pills.filter.secondary.hover
          : theme.pills.filter.primary.color};
    }
  }
  ${(props) => {
    const theme = themeOrDefault(props.theme);
    const {
      pills: {
        filter: { primary, secondary },
      },
    } = theme;

    const regular = secondary.color ?? Core.surface.muted;
    const regularHover = secondary.hover ?? Core.surface.subdued;
    const active = primary.color ?? Core.surface.default;
    const activeHover = primary.hover ?? Core.surface.hover;
    const activeLetter = primary.contrast ?? Core.text.inverse;

    let styles;
    if (props.$hasFilterSelected) {
      styles = css`
        background-color: ${active};
        &:hover {
          background-color: ${activeHover};
        }
      `;
    } else {
      styles = css`
        &:hover {
          border: 1px solid ${activeHover};
        }
      `;
    }

    return css`
      color: ${props.$hasFilterSelected ? activeLetter : regular};
      border: 1px solid ${props.$hasFilterSelected ? activeHover : regularHover};
      ${styles}
    `;
  }};
  ${highVisSlateComponent(HIGH_VIS_FILTER.COMPONENT)}
  &:focus-visible {
    outline: 2px solid -webkit-focus-ring-color;
  }
`;
Pill.displayName = "Filter";
